// Inline Monaco editor pane shown when the dashboard is in "configure"
// mode. Edits flow straight back to the parent (which persists them), so
// switching to "View Render" shows the latest code with no explicit save.
//
// Monaco is loaded by @monaco-editor/react's default loader. The editor
// is lazy (React.lazy) so the chunk only loads when configure mode opens.
//
// `configureMonaco` teaches the TypeScript worker how to read the code we
// actually run: JSX is enabled, every name LiveRender injects as a global
// (components + React hooks) is declared so it doesn't get flagged as an
// undefined reference, and module-resolution errors are ignored because
// imports like `lucide-react` only resolve at runtime via LiveRender's
// require shim. Without this, the worker treats the source as plain TS and
// red-underlines essentially everything.

import { Suspense, lazy } from 'react'
import { Loader2 } from 'lucide-react'
import type { Monaco } from '@monaco-editor/react'
import { useSettings } from '@axilon-apps/libs-ui-dashboard'
import { designPixelsToScaledPixels } from '../../../lib/ui-density'

const MonacoEditor = lazy(() => import('@monaco-editor/react'))

// Every name the sandbox injects as an in-scope global, so the TypeScript
// worker doesn't flag them as undefined. This MIRRORS the `SCOPE` object in
// `apps/dashboard-sandbox/src/render-runtime.ts` (React hooks + live-data
// hooks + the full shadcn palette + Recharts primitives) — keep the two in
// sync when components are added there. Duplicates across groups (e.g.
// `Label`/`Tooltip` exist in both shadcn and Recharts) are de-duped before
// emitting declarations, so listing a name twice here is harmless.
const GLOBAL_NAMES = [
  // Composition primitive — instantiate a saved template at render time.
  'Template',
  // Host-authored review control — placeable by agents, writes only on click.
  'ReviewActions',
  // Canonical application href builders.
  'dashboardHref',
  'dashboardTemplateHref',
  'docsHubDocumentHref',
  'plantHref',
  'tagsTagHref',

  // P&ID / process-mimic
  'AnalogIndicator',
  'At',
  'PidDiagram',
  'PidNode',
  'Pipe',
  'PipePath',
  'Pump',
  'Sensor',
  'Tank',
  'Thermometer',
  'U',
  'Valve',

  // React hooks + Fragment
  'useState',
  'useEffect',
  'useMemo',
  'useRef',
  'useCallback',
  'useReducer',
  'useId',
  'useLayoutEffect',
  'Fragment',

  // Live-data hooks (data-runtime.ts)
  'useActiveAlarms',
  'useAgentFindings',
  'useAgentResults',
  'useAlarmStatus',
  'useCypherQuery',
  'useGatewayStatus',
  'usePowerAnalysis',
  'useSoapOperation',
  'useTagHistory',
  'useDbWrite',
  'useTagWrite',
  'useTagValues',

  // shadcn primitives (alphabetical)
  'Accordion',
  'AccordionContent',
  'AccordionItem',
  'AccordionTrigger',
  'Alert',
  'AlertDescription',
  'AlertDialog',
  'AlertDialogAction',
  'AlertDialogCancel',
  'AlertDialogContent',
  'AlertDialogDescription',
  'AlertDialogFooter',
  'AlertDialogHeader',
  'AlertDialogOverlay',
  'AlertDialogPortal',
  'AlertDialogTitle',
  'AlertDialogTrigger',
  'AlertTitle',
  'AspectRatio',
  'Avatar',
  'AvatarBadge',
  'AvatarFallback',
  'AvatarGroup',
  'AvatarGroupCount',
  'AvatarImage',
  'Badge',
  'Breadcrumb',
  'BreadcrumbEllipsis',
  'BreadcrumbItem',
  'BreadcrumbLink',
  'BreadcrumbList',
  'BreadcrumbPage',
  'BreadcrumbSeparator',
  'Button',
  'ButtonGroup',
  'ButtonGroupSeparator',
  'ButtonGroupText',
  'Calendar',
  'CalendarDayButton',
  'Card',
  'CardAction',
  'CardContent',
  'CardDescription',
  'CardFooter',
  'CardHeader',
  'CardTitle',
  'Carousel',
  'CarouselContent',
  'CarouselItem',
  'CarouselNext',
  'CarouselPrevious',
  'ChartContainer',
  'ChartLegend',
  'ChartLegendContent',
  'ChartStyle',
  'ChartTooltip',
  'ChartTooltipContent',
  'Checkbox',
  'Collapsible',
  'CollapsibleContent',
  'CollapsibleTrigger',
  'Command',
  'CommandDialog',
  'CommandEmpty',
  'CommandGroup',
  'CommandInput',
  'CommandItem',
  'CommandList',
  'CommandSeparator',
  'CommandShortcut',
  'ContextMenu',
  'ContextMenuContent',
  'ContextMenuItem',
  'ContextMenuLabel',
  'ContextMenuSeparator',
  'ContextMenuTrigger',
  'Dialog',
  'DialogClose',
  'DialogContent',
  'DialogDescription',
  'DialogFooter',
  'DialogHeader',
  'DialogOverlay',
  'DialogPortal',
  'DialogTitle',
  'DialogTrigger',
  'Drawer',
  'DrawerClose',
  'DrawerContent',
  'DrawerDescription',
  'DrawerFooter',
  'DrawerHeader',
  'DrawerOverlay',
  'DrawerPortal',
  'DrawerTitle',
  'DrawerTrigger',
  'DropdownMenu',
  'DropdownMenuCheckboxItem',
  'DropdownMenuContent',
  'DropdownMenuGroup',
  'DropdownMenuItem',
  'DropdownMenuLabel',
  'DropdownMenuPortal',
  'DropdownMenuRadioGroup',
  'DropdownMenuRadioItem',
  'DropdownMenuSeparator',
  'DropdownMenuShortcut',
  'DropdownMenuSub',
  'DropdownMenuSubContent',
  'DropdownMenuSubTrigger',
  'DropdownMenuTrigger',
  'Empty',
  'EmptyContent',
  'EmptyDescription',
  'EmptyHeader',
  'EmptyMedia',
  'EmptyTitle',
  'Field',
  'FieldContent',
  'FieldDescription',
  'FieldError',
  'FieldGroup',
  'FieldLabel',
  'FieldLegend',
  'FieldSeparator',
  'FieldSet',
  'FieldTitle',
  'HoverCard',
  'HoverCardContent',
  'HoverCardTrigger',
  'Input',
  'InputGroup',
  'InputGroupAddon',
  'InputGroupButton',
  'InputGroupInput',
  'InputGroupText',
  'InputGroupTextarea',
  'InputOTP',
  'InputOTPGroup',
  'InputOTPSeparator',
  'InputOTPSlot',
  'Item',
  'ItemActions',
  'ItemContent',
  'ItemDescription',
  'ItemFooter',
  'ItemGroup',
  'ItemHeader',
  'ItemMedia',
  'ItemSeparator',
  'ItemTitle',
  'Kbd',
  'KbdGroup',
  'Label',
  'Menubar',
  'MenubarCheckboxItem',
  'MenubarContent',
  'MenubarGroup',
  'MenubarItem',
  'MenubarLabel',
  'MenubarMenu',
  'MenubarPortal',
  'MenubarRadioGroup',
  'MenubarRadioItem',
  'MenubarSeparator',
  'MenubarShortcut',
  'MenubarSub',
  'MenubarSubContent',
  'MenubarSubTrigger',
  'MenubarTrigger',
  'NativeSelect',
  'NativeSelectOptGroup',
  'NativeSelectOption',
  'NavigationMenu',
  'NavigationMenuContent',
  'NavigationMenuIndicator',
  'NavigationMenuItem',
  'NavigationMenuLink',
  'NavigationMenuList',
  'NavigationMenuTrigger',
  'NavigationMenuViewport',
  'Pagination',
  'PaginationContent',
  'PaginationEllipsis',
  'PaginationItem',
  'PaginationLink',
  'PaginationNext',
  'PaginationPrevious',
  'Popover',
  'PopoverAnchor',
  'PopoverContent',
  'PopoverDescription',
  'PopoverHeader',
  'PopoverTitle',
  'PopoverTrigger',
  'Progress',
  'RadioGroup',
  'RadioGroupItem',
  'ResizableHandle',
  'ResizablePanel',
  'ResizablePanelGroup',
  'ScrollArea',
  'ScrollBar',
  'Select',
  'SelectContent',
  'SelectGroup',
  'SelectItem',
  'SelectLabel',
  'SelectScrollDownButton',
  'SelectScrollUpButton',
  'SelectSeparator',
  'SelectTrigger',
  'SelectValue',
  'Separator',
  'Sheet',
  'SheetClose',
  'SheetContent',
  'SheetDescription',
  'SheetFooter',
  'SheetHeader',
  'SheetTitle',
  'SheetTrigger',
  'Sidebar',
  'SidebarContent',
  'SidebarFooter',
  'SidebarGroup',
  'SidebarGroupAction',
  'SidebarGroupContent',
  'SidebarGroupLabel',
  'SidebarHeader',
  'SidebarInput',
  'SidebarInset',
  'SidebarMenu',
  'SidebarMenuAction',
  'SidebarMenuBadge',
  'SidebarMenuButton',
  'SidebarMenuItem',
  'SidebarMenuSkeleton',
  'SidebarMenuSub',
  'SidebarMenuSubButton',
  'SidebarMenuSubItem',
  'SidebarProvider',
  'SidebarRail',
  'SidebarSeparator',
  'SidebarTrigger',
  'Skeleton',
  'Slider',
  'Spinner',
  'Switch',
  'Table',
  'TableBody',
  'TableCaption',
  'TableCell',
  'TableFooter',
  'TableHead',
  'TableHeader',
  'TableRow',
  'Tabs',
  'TabsContent',
  'TabsList',
  'TabsTrigger',
  'Textarea',
  'Toaster',
  'Toggle',
  'ToggleGroup',
  'ToggleGroupItem',
  'Tooltip',
  'TooltipContent',
  'TooltipProvider',
  'TooltipTrigger',

  // Recharts primitives (used inside <ChartContainer>)
  'AreaChart',
  'BarChart',
  'ComposedChart',
  'LineChart',
  'PieChart',
  'RadarChart',
  'RadialBarChart',
  'ScatterChart',
  'Area',
  'Bar',
  'Cell',
  'Line',
  'Pie',
  'PolarAngleAxis',
  'PolarGrid',
  'PolarRadiusAxis',
  'Radar',
  'RadialBar',
  'Scatter',
  'XAxis',
  'YAxis',
  'ZAxis',
  'CartesianGrid',
  'Legend',
  'ReferenceLine',
  'ReferenceArea',
  'ReferenceDot',
  'ResponsiveContainer',
  'LabelList',
  'Brush',
  'Surface',
  'Sector'
]

// Ambient declarations injected so the in-scope globals type-check. `any`
// keeps things permissive (this is a sandbox), and the open JSX namespace
// lets any element/component render without React's own type defs.
const AMBIENT_DTS = `
declare namespace JSX {
  interface IntrinsicElements { [elemName: string]: any }
  type Element = any
  interface ElementClass { render: any }
  interface ElementChildrenAttribute { children: any }
}
declare const React: any
${Array.from(new Set(GLOBAL_NAMES))
  .filter((name) => name !== 'useTagHistory')
  .map((name) => `declare const ${name}: any`)
  .join('\n')}
interface TagHistoryPoint {
  timestamp: string
  tagPath: string
  nodeId: string
  value: number | string | boolean | null
}
interface TagHistoryResult {
  points: TagHistoryPoint[]
  byNodeId: Record<string, TagHistoryPoint[]>
  count: number
}
declare function useTagHistory(options: any): {
  data: TagHistoryResult | null
  error: string | null
  loading: boolean
}
`

let configured = false
function configureMonaco(monaco: Monaco): void {
  if (configured) return
  configured = true

  const ts = monaco.languages.typescript
  const defaults = ts.typescriptDefaults

  defaults.setCompilerOptions({
    ...defaults.getCompilerOptions(),
    jsx: ts.JsxEmit.React,
    target: ts.ScriptTarget.ESNext,
    module: ts.ModuleKind.ESNext,
    moduleResolution: ts.ModuleResolutionKind.NodeJs,
    allowNonTsExtensions: true,
    allowSyntheticDefaultImports: true,
    esModuleInterop: true,
    allowJs: true,
    noEmit: true
  })

  defaults.setDiagnosticsOptions({
    // 2307: cannot find module — imports resolve at runtime, not here.
    // 2792: cannot find module / try moduleResolution — same reason.
    diagnosticCodesToIgnore: [2307, 2792]
  })

  // Stable filePath so repeated mounts replace rather than duplicate.
  defaults.addExtraLib(AMBIENT_DTS, 'ts:dashboard-scope.d.ts')
}

export interface CodeEditorPaneProps {
  code: string
  onChange: (code: string) => void
  /**
   * Surfaces the mounted Monaco editor instance so the host can do
   * cursor-aware insertions (e.g. the graph-backed tag picker inserting a
   * validated graph node ID into the TSX). Typed loosely to keep monaco's editor
   * types out of the public prop surface.
   */
  onEditorMount?: (editor: MonacoCodeEditor) => void
}

/** Minimal surface of monaco's ICodeEditor that insertions need. */
export interface MonacoCodeEditor {
  getSelection(): unknown
  executeEdits(
    source: string,
    edits: Array<{ range: unknown; text: string; forceMoveMarkers?: boolean }>
  ): boolean
  focus(): void
}

const CodeEditorPane = ({ code, onChange, onEditorMount }: CodeEditorPaneProps) => {
  // Follow the app's resolved color scheme so the editor doesn't clash
  // with a light/dark UI. Monaco ships 'vs' (light) and 'vs-dark'.
  const { settings } = useSettings()
  const theme = settings.colorScheme === 'dark' ? 'vs-dark' : 'vs'

  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center gap-2 text-sm text-ink-3">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Loading editor…
        </div>
      }
    >
      <MonacoEditor
        height="100%"
        defaultLanguage="typescript"
        language="typescript"
        path="dashboard.tsx"
        theme={theme}
        beforeMount={configureMonaco}
        onMount={(editor) => onEditorMount?.(editor as unknown as MonacoCodeEditor)}
        value={code}
        onChange={(value) => onChange(value ?? '')}
        options={{
          fontSize: designPixelsToScaledPixels(13),
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          wordWrap: 'on'
        }}
      />
    </Suspense>
  )
}

export default CodeEditorPane
